import React, { useState } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import styled, { withTheme } from "styled-components";
import { Form, Field, FormSpy } from "react-final-form";
import { get, isObject, toNumber } from "lodash";
import TextInput from "fields/TextInput";
import Button from "components/Button";
import MagIcon from "components/icons/magglass";
import LocationIcon from "components/icons/location";
import Dropdown from "fields/SelectDropdown";
import AddressDropdown from "fields/AddressDropdown";
import StorefrontIcon from "components/icons/storefront";
import useCurrentLocation, {
  useLocationChange,
  useFallbackLocation,
} from "hooks/use-current-location";

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1140px;
  width: 100%;
`;

const OuterWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  display: flex;
  justify-content: flex-start;
  padding: 20px 25px 20px 25px;

  ${({ theme }) => theme.mq.lg`
    padding: 20px 30px 20px 30px;
  `}
`;

const ServiceTypesDropdown = styled(Dropdown)``;

const SearchForm = styled.form`
  display: grid;
  margin: 0 auto;
  row-gap: 1.875rem;
  width: 100%;
  column-gap: 1.25rem;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr 1fr;

  ${({ theme }) => theme.mq.lg`
    grid-template-columns: 1fr 1fr auto 1fr  .5fr;
    grid-template-rows: 1fr;
  `}
`;

const SearchButton = styled(Button)`
  grid-row: 4 / 5;

  ${({ theme }) => theme.mq.lg`
    grid-column: auto;
    grid-row: auto;
    width: 9.5625rem;
  `}
`;

const StyledAddressDropdown = styled(AddressDropdown)`
  svg {
    margin-top: 0.1875rem;
  }
`;

const VerticalLineBreak = styled.hr`
  display: none;
  ${({ theme }) => theme.mq.lg`
    display: inherit;
    height: 2.25rem;
    color: ${theme.themeColors.darkGreen3};
  `}
`;

const StyledMagIcon = styled(MagIcon)``;

const StyledTextInput = styled(TextInput)``;

const serviceTypeOptions = [
  {
    value: "all",
    label: "All Services",
  },
  {
    value: "grooming",
    label: "Grooming",
  },
  {
    value: "training",
    label: "Training",
  },
  {
    value: "veterinary",
    label: "Veterinary",
  },
];
function BusinessSearchBar({ theme, setServices }) {
  const router = useRouter();
  const {
    query: { formattedAddress, latitude, longitude },
  } = router;
  const currentLocation = useCurrentLocation();
  const fallbackLocation = useFallbackLocation();
  const [address, setAddress] = useState({
    formatted_address:
      formattedAddress ||
      currentLocation?.formatted_address ||
      fallbackLocation.formatted_address,
    latitude:
      latitude || currentLocation?.latitude || fallbackLocation.latitude,
    longitude:
      longitude || currentLocation?.longitude || fallbackLocation.longitude,
  });

  const { pageSize } = router.query;

  const initialStartDateDisplay = router.query.startDateTime
    ? toNumber(router.query.startDateTime)
    : undefined;

  const handleLocationChange = useLocationChange();

  const handleSearch = (values) => {
    const lat = get(values.location, "latitude", undefined);
    const lng = get(values.location, "longitude", undefined);
    const formattedAddressVal = get(values.location, "formatted_address", "");
    router.push({
      query: {
        currentPage: 1,
        ...(formattedAddressVal && { formattedAddress: formattedAddressVal }),
        ...(pageSize && { pageSize }),
        ...(lat && { latitude: lat }),
        ...(lng && { longitude: lng }),
        ...(values.s && { s: values.s }),
        ...(values.q && { q: values.q }),
        ...(values.startDateTime && { startDateTime: values.startDateTime }),
      },
    });
  };

  return (
    <OuterWrapper>
      <InnerWrapper>
        <Form
          onSubmit={handleSearch}
          initialValues={{
            q: router.query.q,
            s: router.query.s,
            startDateTime: initialStartDateDisplay,
            location: address,
          }}
          validate={(values) => {
            const errors = {};
            if (!values?.location?.formatted_address) {
              errors.location = "Required";
            }
            if (!values?.location?.latitude) {
              errors.location = "Required";
            }
            if (!values?.location?.longitude) {
              errors.location = "Required";
            }

            return errors;
          }}
          render={({ handleSubmit, submitting }) => {
            return (
              <SearchForm>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values: { location, s } }) => {
                    if (isObject(location) && location.formatted_address) {
                      setAddress(address);
                      handleLocationChange(address);
                    }
                    setServices(s);
                  }}
                />

                <Field
                  name="location"
                  render={(props) => (
                    <StyledAddressDropdown
                      isSearch
                      label="Location"
                      placeholder="Location"
                      LeadingIcon={
                        <LocationIcon color={theme.themeColors.darkGreen1} />
                      }
                      {...props}
                    />
                  )}
                />
                <Field
                  name="s"
                  render={(props) => (
                    <ServiceTypesDropdown
                      highlightColor="green"
                      placeholder="Service"
                      options={serviceTypeOptions}
                      LeadingIcon={
                        <StorefrontIcon color={theme.themeColors.darkGreen1} />
                      }
                      {...props}
                    />
                  )}
                />
                <VerticalLineBreak />
                <Field
                  name="q"
                  render={(props) => (
                    <StyledTextInput
                      label="Search"
                      placeholder="Business Name (Optional)"
                      LeadingIcon={() => (
                        <StyledMagIcon color={theme.themeColors.darkGreen1} />
                      )}
                      {...props}
                    />
                  )}
                />

                <SearchButton
                  disabled={submitting}
                  onClick={handleSubmit}
                  role="button"
                  type="submit"
                >
                  Search
                </SearchButton>
              </SearchForm>
            );
          }}
        />
      </InnerWrapper>
    </OuterWrapper>
  );
}

export default withTheme(BusinessSearchBar);

BusinessSearchBar.propTypes = {
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      darkGreen1: PropTypes.string,
    }),
  }),
  setServices: PropTypes.func,
};

BusinessSearchBar.defaultProps = {
  theme: undefined,
  setServices: () => {},
};
